import type { ReactNode } from "react";
import type { ContenidoAyuda, ItemAyuda } from "@/ui/AyudaVista";
import { ESTADO_IMEI_PILL, type EstadoImeiUI } from "@/lib/imei";

/**
 * Ayuda de la pestaña IMEIs del caso. Lo que antes iba en los tooltips de los
 * botones de estado (OK/REV/…/Auto) y de la marca de Ventas.
 *
 * Mismas condiciones que `components/TabImeis`: todos los botones (estado,
 * agregar, editar, quitar) salen a admin y soporte, y solo con el caso abierto.
 */

const ESTADOS: [EstadoImeiUI, string][] = [
  ["OK", "Sin pendientes: en orden o revisado hace poco."],
  ["REV", "En revisión."],
  ["CLI", "Esperando al cliente."],
  ["PROVEEDOR", "Con proveedor (el botón dice PROV): el equipo se envió físicamente al proveedor."],
  ["CRIT", "Crítico: marcado a mano, o 7 o más días sin movimiento."],
];

function Ficha({ cls, children }: { cls: string; children: ReactNode }) {
  return (
    <span className={`inline-flex items-center whitespace-nowrap rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${cls}`}>
      {children}
    </span>
  );
}

export function ayudaImeis({
  puedeEditar,
  cerrado,
}: {
  /** Admin o soporte Y caso abierto. */
  puedeEditar: boolean;
  cerrado: boolean;
}): ContenidoAyuda {
  const botones: ItemAyuda[] = [
    ...(puedeEditar
      ? [
          {
            label: "OK · REV · CLI · PROV · CRIT",
            text: "Fijan a mano el estado del equipo (el resaltado es el actual). El cambio queda en la bitácora; marcarlo CRIT además le avisa al asignado, si no eres tú.",
          },
          {
            label: "Auto",
            text: "Quita el estado puesto a mano: el semáforo vuelve a cambiar solo con los días sin movimiento (menos de 4 días OK, de 4 a 6 REV, 7 o más CRIT), contando desde hoy.",
          },
          {
            label: "+ Agregar IMEI",
            text: "Agrega uno o varios equipos, separados por coma, espacio o renglón (sirve pegar desde Excel). Toman el modelo del caso; si es otro modelo, abre un caso nuevo.",
          },
          {
            label: "Editar",
            text: "Corrige el número del IMEI. El modelo no se pide: es el del caso.",
          },
          {
            label: "Quitar",
            text: "Quita el equipo del caso, después de confirmar. Su historial de estado se borra y queda una nota en la bitácora.",
          },
          {
            label: "📌 Datos de referencia: Agregar / Editar",
            text: "Notas fijas SOLO de ese equipo (teléfono del cliente, contacto en sitio…). No es evidencia ni bitácora.",
          },
        ]
      : []),
    {
      label: "✓ ventas / ⚠ sin venta",
      text: "Tócalo para buscar ese IMEI en Ventas (se abre en otra pestaña).",
    },
  ];

  return {
    manual: "semaforo-imei",
    intro: (
      <p>
        Los equipos del caso con su estado de revisión. Los que están en CRIT suben hasta
        arriba.
        {cerrado && (
          <>
            {" "}
            Con el caso cerrado los equipos son de solo lectura y todos se ven en verde (OK), sin
            contar días: ya no hay nada que hacer ahí. No se agregan, editan, quitan ni se les
            cambia el estado. Si un equipo volvió a fallar, o es uno nuevo, usa «+ Caso
            relacionado» en el encabezado.
          </>
        )}
      </p>
    ),
    secciones: [
      { titulo: "Qué hace cada botón", items: botones },
      {
        titulo: "Qué significa cada indicador",
        items: [
          ...ESTADOS.map(([e, text]) => ({
            label: <Ficha cls={ESTADO_IMEI_PILL[e]}>{e}</Ficha>,
            text,
          })),
          {
            label: "✓ ventas",
            text: "El IMEI aparece en nuestras ventas; debajo dice a quién y cuándo se vendió, y si ya tuvo garantía.",
          },
          {
            label: "⚠ sin venta",
            text: "No aparece en Ventas: puede que no lo hayamos vendido nosotros, o que ese mes aún no esté cargado. Es solo un aviso, no bloquea nada.",
          },
          {
            label: "Última revisión · N días sin movimiento",
            text: "Cuándo se movió el equipo por última vez (un cambio de estado cuenta) y cuántos días van desde entonces.",
          },
        ],
      },
    ],
  };
}
